import { useState } from 'react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { CheckCircle, Clock, AlertCircle, Bell, ChevronDown, ChevronUp } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useCollaborations } from '@/hooks/useCollaborations'
import { deriveCollaborationState } from '@adthlete/common/utils/collaborationState'
import { CreateCollaborationModal } from '@/components/CreateCollaborationModal'
import {
  useAcceptCampaignMutation,
  useRejectCampaignMutation,
  useRestoreCampaignMutation,
} from '@/redux/collaborationsApi'
import { toast } from '@/components/ui/sonner'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { formatDateOnly } from '@/lib/dateOnly'

const Campaigns = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { collaborations, pendingBrand = [], isLoading: loading, refetch } = useCollaborations()
  const [expandedBid, setExpandedBid] = useState<string | number | null>(null)
  const [showRejected, setShowRejected] = useState(false)

  const [acceptCampaign] = useAcceptCampaignMutation()
  const [rejectCampaign] = useRejectCampaignMutation()
  const [restoreCampaign] = useRestoreCampaignMutation()

  // Filter collaborations by action owner using deriveCollaborationState
  // Exclude 'offered' and 'rejected' from the general pool as they are handled in Offers tab
  const activeCampaigns = collaborations.filter(
    (c) => c.status !== 'pending_brand' && c.status !== 'offered' && c.status !== 'rejected',
  )

  const offeredCampaigns = collaborations.filter((c) => c.status === 'offered')
  const rejectedCampaigns = collaborations.filter((c) => c.status === 'rejected')

  // Accepted campaigns where athlete needs to take action
  const acceptedCampaigns = activeCampaigns.filter((c) => {
    const state = deriveCollaborationState(c as any)
    return state.actionOwner === 'athlete' && !state.isComplete
  })

  // Campaigns where athlete is waiting on the brand
  const waitingOnBrandCampaigns = activeCampaigns.filter((c) => {
    const state = deriveCollaborationState(c as any)
    return state.actionOwner === 'marketer' && !state.isComplete
  })

  // Combine waiting on brand (active) and pending brand (invitations)
  const allWaitingOnBrand = [...waitingOnBrandCampaigns, ...pendingBrand]

  // Completed campaigns
  const completedCampaigns = activeCampaigns.filter((c) => {
    const state = deriveCollaborationState(c as any)
    return state.isComplete
  })

  // --- Handlers for Offers ---

  const handleAccept = async (id: string | number) => {
    try {
      await acceptCampaign(id.toString()).unwrap()
      toast('Offer Accepted', {
        description: 'The collaboration has been added to your dashboard.',
      })
      refetch()
    } catch (error) {
      toast.error('Error', { description: 'Failed to accept the bid. Please try again.' })
    }
  }

  const handleReject = async (id: string | number) => {
    try {
      await rejectCampaign(id.toString()).unwrap()
      toast('Offer Declined', { description: 'The collaboration offer has been declined.' })
      refetch()
    } catch (error) {
      toast.error('Error', { description: 'Failed to reject the bid. Please try again.' })
    }
  }

  const handleRestore = async (id: string | number) => {
    try {
      await restoreCampaign(id.toString()).unwrap()
      toast('Offer Restored', {
        description: 'The collaboration has been moved back to active offers.',
      })
      refetch()
    } catch (error) {
      toast.error('Error', { description: 'Failed to restore the offer. Please try again.' })
    }
  }

  const toggleExpand = (id: string | number) => {
    setExpandedBid(expandedBid === id ? null : id)
  }

  const formatPaymentAmount = (paymentAmount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(paymentAmount)
  }

  const getBidTimeRemaining = (bidReceivedAt: string | undefined, id: string | number) => {
    if (!bidReceivedAt) return 'Unknown'
    const bidTime = new Date(bidReceivedAt)
    const now = new Date()
    const expirationTime = new Date(bidTime.getTime() + 48 * 60 * 60 * 1000)
    const hoursRemaining = Math.round(
      (expirationTime.getTime() - now.getTime()) / (1000 * 60 * 60),
    )
    if (hoursRemaining <= 0) {
      return 'Expiring soon'
    } else {
      return `${hoursRemaining} hours left`
    }
  }

  // --- Render Helpers ---

  const CampaignCard = ({
    campaign: collaboration,
    navigate,
  }: {
    campaign: any
    navigate: any
  }) => {
    const [imgError, setImgError] = useState(false)
    const hasValidLogo =
      collaboration.brand?.logo &&
      typeof collaboration.brand.logo === 'string' &&
      collaboration.brand.logo.trim().length > 0

    const dueDate =
      formatDateOnly(collaboration.contentDueDate, undefined, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      }) ??
      formatDateOnly(collaboration.endDate, undefined, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      }) ??
      'TBD'

    const hasRevisionRequested = collaboration.milestones?.some(
      (m: any) => m.status === 'revision_requested',
    )

    return (
      <Card
        className="bg-white/10 backdrop-blur-xs border-white/10 hover:bg-white/15 transition-colors cursor-pointer"
        onClick={() => navigate(`/collaborations/${collaboration.id}`)}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className="h-16 w-16 shrink-0 rounded overflow-hidden bg-white/20 flex items-center justify-center">
              {hasValidLogo && !imgError ? (
                <img
                  src={collaboration.brand.logo}
                  alt={collaboration.brand?.name}
                  className="h-full w-full object-cover"
                  onError={() => setImgError(true)}
                />
              ) : (
                <span className="text-2xl font-bold text-white/50">
                  {collaboration.brand?.name
                    ? collaboration.brand.name.charAt(0).toUpperCase()
                    : 'C'}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-medium text-white truncate">{collaboration.title}</h3>
                {hasRevisionRequested && (
                  <Badge className="shrink-0 bg-orange-500/20 text-orange-400 border-orange-500/30 text-[10px] font-medium">
                    Revisions Requested
                  </Badge>
                )}
              </div>
              <p className="text-sm text-gray-400 mb-2">{collaboration.brand?.name}</p>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-300">
                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                    collaboration.paymentAmount,
                  )}
                </span>
                <span className="text-gray-400">Due: {dueDate}</span>
              </div>
            </div>
            <Badge variant="outline" className="border-white/20 text-white shrink-0">
              {Array.isArray(collaboration.platform)
                ? collaboration.platform.join(', ') || '—'
                : collaboration.platform || '—'}
            </Badge>
          </div>
        </CardContent>
      </Card>
    )
  }

  const OfferCard = ({ bid, isArchived }: { bid: any; isArchived?: boolean }) => {
    return (
      <Card variant="dark" className="overflow-hidden card-hover">
        <div className="p-4 cursor-pointer hover:bg-white/5" onClick={() => toggleExpand(bid.id)}>
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 shrink-0 rounded overflow-hidden bg-white/20 flex items-center justify-center">
              {bid.brand?.logo ? (
                <img
                  src={bid.brand.logo}
                  alt={bid.brand.name}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
              ) : (
                <span className="text-xl font-bold text-white/50">
                  {bid.brand?.name ? bid.brand.name.charAt(0).toUpperCase() : '?'}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="max-w-[60%]">
                  <h3 className="text-lg font-medium text-white truncate">{bid.title}</h3>
                  <p className="text-sm text-gray-400 truncate">
                    {bid.brand.name} •{' '}
                    {Array.isArray(bid.platform) ? bid.platform.join(', ') : bid.platform}
                  </p>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-white">
                    {bid.paymentAmount ? formatPaymentAmount(bid.paymentAmount) : 'TBD'}
                  </div>
                  {isArchived ? (
                    <span className="text-sm text-gray-500">Declined</span>
                  ) : (
                    <span className="text-sm text-[#C0FD72] flex items-center justify-end">
                      <Clock className="h-3 w-3 mr-1" />
                      {getBidTimeRemaining(bid.bidReceivedAt, bid.id)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {expandedBid === bid.id && (
          <div className="p-4 border-t border-white/10 bg-white/5">
            <div className="flex flex-col md:flex-row gap-4">
              {bid.imageUrl && (
                <div className="md:w-1/3 rounded-lg overflow-hidden bg-black/20 flex items-center justify-center min-h-80">
                  <img
                    src={bid.imageUrl}
                    alt={`${bid.title} preview`}
                    className="max-w-full max-h-80 object-contain"
                  />
                </div>
              )}

              <div className={bid.imageUrl ? 'md:w-2/3' : 'w-full'}>
                <div className="mb-4 flex items-center gap-3">
                  <div className="h-16 w-16 rounded overflow-hidden bg-white/20 flex items-center justify-center">
                    {bid.brand?.logo ? (
                      <img
                        src={bid.brand.logo}
                        alt={bid.brand.name}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          const target = e.currentTarget
                          target.style.display = 'none'
                          const parent = target.parentElement
                          if (parent) {
                            parent.innerHTML = `<span class="text-2xl font-bold text-white/50">${bid.brand?.name ? bid.brand.name.charAt(0).toUpperCase() : '?'}</span>`
                          }
                        }}
                      />
                    ) : (
                      <span className="text-2xl font-bold text-white/50">
                        {bid.brand?.name ? bid.brand.name.charAt(0).toUpperCase() : '?'}
                      </span>
                    )}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-400">BRAND</h4>
                    <p className="text-lg font-semibold text-white">{bid.brand.name}</p>
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="font-medium text-white mb-1 text-sm">BRIEF</h4>
                  <p className="text-gray-300">{bid.brief}</p>
                </div>

                {bid.script && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-white mb-1">AUDIO SCRIPT</h4>
                    <p className="text-gray-300 bg-black/20 p-3 rounded border border-white/10">
                      "{bid.script}"
                    </p>
                  </div>
                )}

                {bid.instructions && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-white mb-1">INSTRUCTIONS</h4>
                    <p className="text-gray-300">{bid.instructions}</p>
                  </div>
                )}

                {(bid.startDate || bid.endDate || bid.contentDueDate) && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-white mb-2">TIMELINE</h4>
                    <div className="space-y-1 text-sm">
                      {bid.startDate && (
                        <p className="text-gray-300">
                          <span className="text-gray-400">Start:</span>{' '}
                          {formatDateOnly(bid.startDate, undefined, {
                            year: 'numeric',
                            month: 'numeric',
                            day: 'numeric',
                          }) ?? 'TBD'}
                        </p>
                      )}
                      {bid.contentDueDate && (
                        <p className="text-gray-300">
                          <span className="text-gray-400">Content Due:</span>{' '}
                          {formatDateOnly(bid.contentDueDate, undefined, {
                            year: 'numeric',
                            month: 'numeric',
                            day: 'numeric',
                          }) ?? 'TBD'}
                        </p>
                      )}
                      {bid.endDate && (
                        <p className="text-gray-300">
                          <span className="text-gray-400">End:</span>{' '}
                          {formatDateOnly(bid.endDate, undefined, {
                            year: 'numeric',
                            month: 'numeric',
                            day: 'numeric',
                          }) ?? 'TBD'}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap justify-end gap-3 mt-6">
                  {isArchived ? (
                    <Button
                      onClick={() => handleRestore(bid.id)}
                      variant="outline"
                      className="border border-white/20 text-gray-300 hover:bg-white/10 hover:text-white"
                    >
                      Restore
                    </Button>
                  ) : (
                    <>
                      <p className="text-xs text-gray-400 w-full text-right mb-1">
                        Visit the offer details page to negotiate terms and accept.
                      </p>
                      <Button
                        onClick={() => handleReject(bid.id)}
                        variant="outline"
                        className="border border-white/20 text-gray-300 hover:bg-white/10 hover:text-white"
                      >
                        Decline
                      </Button>
                      <Button variant="lime" onClick={() => navigate(`/bids/${bid.id}`)}>
                        View Offer Details
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>
    )
  }

  const renderEmptyState = (title: string, description: string, icon: React.ReactNode) => (
    <Card variant="dark" className="p-8 text-center">
      <div className="flex justify-center mb-4">{icon}</div>
      <h3 className="text-xl font-semibold mb-2 text-white">{title}</h3>
      <p className="text-gray-400">{description}</p>
    </Card>
  )
  // Tab logic
  const validTabs = ['offers', 'accepted', 'waiting', 'completed']
  const tabFromUrl = searchParams.get('tab')
  const activeTab = validTabs.includes(tabFromUrl || '') ? tabFromUrl || 'accepted' : 'accepted'

  const handleTabChange = (newTab: string) => {
    if (validTabs.includes(newTab)) {
      setSearchParams({ tab: newTab })
    }
  }

  return (
    <div className="pb-4 max-w-full">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Collaborations</h1>
          <p className="text-gray-400 text-sm">
            View and manage all your collaborations and offers
          </p>
        </div>
        <div className="shrink-0">
          <CreateCollaborationModal />
        </div>
      </div>

      {loading && collaborations.length === 0 ? (
        <Card variant="dark" className="p-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C0FD72]"></div>
          </div>
          <p className="text-gray-400">Loading collaborations...</p>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="w-full mb-4 bg-white/10 border border-white/10 overflow-x-auto justify-start">
            <TabsTrigger
              value="offers"
              className="flex-none data-[state=active]:bg-[#C0FD72] data-[state=active]:text-black data-[state=inactive]:bg-transparent data-[state=inactive]:text-white font-medium"
            >
              Offers ({offeredCampaigns.length})
            </TabsTrigger>
            <TabsTrigger
              value="accepted"
              className="flex-none data-[state=active]:bg-[#C0FD72] data-[state=active]:text-black data-[state=inactive]:bg-transparent data-[state=inactive]:text-white font-medium"
            >
              Accepted ({acceptedCampaigns.length})
            </TabsTrigger>
            <TabsTrigger
              value="waiting"
              className="flex-none data-[state=active]:bg-[#C0FD72] data-[state=active]:text-black data-[state=inactive]:bg-transparent data-[state=inactive]:text-white font-medium"
            >
              Waiting on Brand ({allWaitingOnBrand.length})
            </TabsTrigger>
            <TabsTrigger
              value="completed"
              className="flex-none data-[state=active]:bg-[#C0FD72] data-[state=active]:text-black data-[state=inactive]:bg-transparent data-[state=inactive]:text-white font-medium"
            >
              Completed ({completedCampaigns.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="offers">
            <div className="space-y-6">
              {offeredCampaigns.length > 0 && (
                <Alert className="glass-card border-blue-500/30 text-white bg-[#f9da4c]">
                  <div className="flex items-center">
                    <div className="w-14 h-14 rounded-full border border-black flex items-center justify-center mr-3 shrink-0">
                      <Bell className="h-5 w-5 text-[#000000]" />
                    </div>
                    <div>
                      <AlertTitle className="text-slate-950">
                        Collaboration offers expire in 48 hours
                      </AlertTitle>
                      <AlertDescription className="text-slate-950">
                        Offers that are not accepted within 48 hours will automatically expire.
                      </AlertDescription>
                    </div>
                  </div>
                </Alert>
              )}

              {offeredCampaigns.length > 0 ? (
                <div className="space-y-4">
                  {offeredCampaigns.map((c) => (
                    <OfferCard key={c.id} bid={c} />
                  ))}
                </div>
              ) : (
                renderEmptyState(
                  'No New Offers',
                  "You don't have any new collaboration offers at the moment.",
                  <AlertCircle className="h-12 w-12 text-[#C0FD72]" />,
                )
              )}

              {/* Rejected Offers Link/Section */}
              <div className="pt-4 border-t border-white/10">
                <Button
                  variant="ghost"
                  onClick={() => setShowRejected(!showRejected)}
                  className="text-gray-400 flex items-center gap-2"
                >
                  {showRejected ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  {showRejected ? 'Hide Declined Offers' : 'Show Declined Offers'}
                </Button>

                {showRejected && (
                  <div className="mt-4 space-y-4">
                    {rejectedCampaigns.length > 0 ? (
                      rejectedCampaigns.map((c) => (
                        <OfferCard key={c.id} bid={c} isArchived={true} />
                      ))
                    ) : (
                      <p className="text-gray-500 italic pl-4">No declined offers found.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="accepted">
            <p className="text-sm text-gray-400 mb-4">
              Collaborations where you need to take action. Review and complete your tasks.
            </p>
            {acceptedCampaigns.length > 0 ? (
              <div className="space-y-4">
                {acceptedCampaigns.map((c) => (
                  <CampaignCard key={c.id} campaign={c} navigate={navigate} />
                ))}
              </div>
            ) : (
              renderEmptyState(
                'No Action Required',
                "You don't have any collaborations requiring your attention right now.",
                <AlertCircle className="h-12 w-12 text-[#C0FD72]" />,
              )
            )}
          </TabsContent>

          <TabsContent value="waiting">
            <p className="text-sm text-gray-400 mb-4">
              Collaborations where you're waiting for the brand to review or respond.
            </p>
            {allWaitingOnBrand.length > 0 ? (
              <div className="space-y-4">
                {allWaitingOnBrand.map((c) => (
                  <CampaignCard key={c.id} campaign={c} navigate={navigate} />
                ))}
              </div>
            ) : (
              renderEmptyState(
                'Nothing Pending',
                "You're not waiting on any brand responses at the moment.",
                <Clock className="h-12 w-12 text-orange-400" />,
              )
            )}
          </TabsContent>

          <TabsContent value="completed">
            <p className="text-sm text-gray-400 mb-4">
              Collaborations you've successfully completed.
            </p>
            {completedCampaigns.length > 0 ? (
              <div className="space-y-4">
                {completedCampaigns.map((c) => (
                  <CampaignCard key={c.id} campaign={c} navigate={navigate} />
                ))}
              </div>
            ) : (
              renderEmptyState(
                'No Completed Collaborations',
                "You haven't completed any collaborations yet.",
                <CheckCircle className="h-12 w-12 text-[#C0FD72]" />,
              )
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  )
}

export default Campaigns
